import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../public Func/axiosAuth';
import { SearchBar, LoadingSkeleton, Button } from '../../components/ui';
import './AdminQA.css';

const TABS = {
  PENDING: 'pending',
  ANSWERED: 'answered',
};

function AdminQAPage() {
  const [activeTab, setActiveTab] = useState(TABS.PENDING);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [submitting, setSubmitting] = useState(null);
  const [loadBlock, setLoadBlock] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [answerTexts, setAnswerTexts] = useState({});
  const [error, setError] = useState(null);

  const fetchQuestions = useCallback(async (block = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setQuestions([]);
    }
    setError(null);

    try {
      const endpoint = activeTab === TABS.PENDING
        ? '/qa/pending_questions'
        : '/qa/questions';

      const params = { loadBlock: block };
      if (searchQuery) params.searchQuery = searchQuery;
      if (activeTab === TABS.ANSWERED) params.sortBy = 'answer_date';

      const res = await axios.get(process.env.REACT_APP_BACKEND_URL + endpoint, { params });
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];

      if (append) {
        setQuestions(prev => [...prev, ...data]);
      } else {
        setQuestions(data);
      }

      setHasMore(data.length > 0);
      setLoadBlock(block);
    } catch (err) {
      console.error('Error fetching questions:', err);
      setError('Failed to load questions');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeTab, searchQuery]);

  useEffect(() => {
    setLoadBlock(1);
    setHasMore(true);
    fetchQuestions(1);
  }, [activeTab]);

  useEffect(() => {
    setLoadBlock(1);
    setHasMore(true);
    fetchQuestions(1);
  }, [searchQuery]);

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchQuestions(loadBlock + 1, true);
    }
  };

  const handleAnswerSubmit = async (questionID) => {
    const answer = answerTexts[questionID];
    if (!answer?.trim()) return;

    setSubmitting(questionID);
    setError(null);

    try {
      await axios.post(process.env.REACT_APP_BACKEND_URL + '/admin/qa/answer', {
        questionID,
        answer: answer.trim(),
      });

      setAnswerTexts(prev => ({ ...prev, [questionID]: '' }));
      setExpandedId(null);
      fetchQuestions(1);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to submit answer';
      setError(msg);
    } finally {
      setSubmitting(null);
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(prev => prev === id ? null : id);
  };

  const handleAnswerChange = (id, value) => {
    setAnswerTexts(prev => ({ ...prev, [id]: value }));
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
  };

  return (
    <div className="qa-page">
      <div className="qa-header">
        <div>
          <h1 className="page-title">Q&A Management</h1>
          <p className="page-subtitle">
            {loading ? 'Loading...' : `${questions.length} ${questions.length === 1 ? 'question' : 'questions'}`}
          </p>
        </div>
      </div>

      <div className="qa-tabs">
        <button
          className={`qa-tab ${activeTab === TABS.PENDING ? 'qa-tab-active' : ''}`}
          onClick={() => setActiveTab(TABS.PENDING)}
        >
          Pending Questions
        </button>
        <button
          className={`qa-tab ${activeTab === TABS.ANSWERED ? 'qa-tab-active' : ''}`}
          onClick={() => setActiveTab(TABS.ANSWERED)}
        >
          Answered Questions
        </button>
      </div>

      <div className="qa-search-section">
        <SearchBar
          value={searchQuery}
          onChange={handleSearch}
          placeholder="Search questions..."
          size="lg"
          fullWidth
          disabled={loading}
        />
      </div>

      {error && (
        <div className="qa-error">
          {error}
        </div>
      )}

      <div className="qa-list">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="qa-skeleton">
              <LoadingSkeleton width="70%" height="22px" />
              <LoadingSkeleton width="100%" height="16px" style={{ marginTop: '12px' }} />
              <LoadingSkeleton width="40%" height="14px" style={{ marginTop: '8px' }} />
            </div>
          ))
        ) : questions.length > 0 ? (
          <>
            {questions.map((q) => (
              <div
                key={q.id}
                className={`qa-card ${expandedId === q.id ? 'qa-card-expanded' : ''}`}
              >
                <div className="qa-card-header" onClick={() => toggleExpand(q.id)}>
                  <div className="qa-card-info">
                    <div className="qa-card-question">
                      <span className="qa-question-text">{q.question}</span>
                    </div>
                    <div className="qa-card-meta">
                      <span className="qa-meta-item">
                        {new Date(q.question_date).toLocaleDateString()}
                      </span>
                      {q.answer_date && (
                        <span className="qa-meta-item">
                          Answered: {new Date(q.answer_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="qa-card-toggle">
                    <svg
                      width="20" height="20" viewBox="0 0 24 24"
                      fill="none" stroke="currentColor"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                      style={{
                        transform: expandedId === q.id ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                      }}
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                </div>

                {expandedId === q.id && (
                  <div className="qa-card-body">
                    <div className="qa-detail-row">
                      <span className="qa-detail-label">Question:</span>
                      <span className="qa-detail-value">{q.question}</span>
                    </div>
                    <div className="qa-detail-row">
                      <span className="qa-detail-label">Date:</span>
                      <span className="qa-detail-value">
                        {new Date(q.question_date).toLocaleDateString()}
                      </span>
                    </div>
                    {q.patient && (
                      <div className="qa-detail-row">
                        <span className="qa-detail-label">Patient:</span>
                        <span className="qa-detail-value">
                          {q.patient.name || q.patient.id}
                        </span>
                      </div>
                    )}

                    {q.answer ? (
                      <>
                        <div className="qa-detail-divider" />
                        <div className="qa-detail-row">
                          <span className="qa-detail-label">Answer:</span>
                          <span className="qa-detail-value">{q.answer}</span>
                        </div>
                        {q.admin && !q.doctor ? (
                          <div className="qa-detail-row">
                            <span className="qa-detail-label">Answered by:</span>
                            <span className="qa-detail-value">
                              Admin {q.admin.name || q.admin.id}
                            </span>
                          </div>
                        ) : q.doctor ? (
                          <div className="qa-detail-row">
                            <span className="qa-detail-label">Doctor:</span>
                            <span className="qa-detail-value">
                              {q.doctor.name || q.doctor.id}
                            </span>
                          </div>
                        ) : null}
                        <div className="qa-detail-row">
                          <span className="qa-detail-label">Answered:</span>
                          <span className="qa-detail-value">
                            {new Date(q.answer_date).toLocaleDateString()}
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="qa-detail-divider" />
                        <div className="qa-answer-form">
                          <textarea
                            className="qa-answer-textarea"
                            placeholder="Write your answer..."
                            value={answerTexts[q.id] || ''}
                            onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                            rows={4}
                            disabled={submitting === q.id}
                          />
                          <Button
                            variant="primary"
                            onClick={() => handleAnswerSubmit(q.id)}
                            disabled={!answerTexts[q.id]?.trim() || submitting === q.id}
                          >
                            {submitting === q.id ? 'Submitting...' : 'Submit Answer'}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}

            {loadingMore && (
              <div className="qa-loading-more">
                <LoadingSkeleton width="100%" height="120px" />
              </div>
            )}

            {hasMore && !loadingMore && (
              <div className="qa-load-more">
                <Button
                  variant="secondary"
                  onClick={handleLoadMore}
                  fullWidth
                  disabled={loadingMore}
                >
                  Load More
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="qa-empty">
            <div className="qa-empty-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 className="qa-empty-title">
              {activeTab === TABS.PENDING ? 'No pending questions' : 'No answered questions'}
            </h3>
            <p className="qa-empty-description">
              {searchQuery
                ? 'Try adjusting your search criteria'
                : activeTab === TABS.PENDING
                  ? 'All questions have been answered'
                  : 'No questions have been answered yet'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminQAPage;
